import { ComponentResource, DesignDetailResource, PatchComponentsByComponentApiArg, PatchComponentsByComponentApiResponse, PatchComponentsByComponentReplaceCastInChannelApiArg, PatchComponentsByComponentReplaceTBoltApiArg, PostComponentsApiArg, PostComponentsApiResponse, serverApi } from '@/api/serverApi'
import { type PayloadAction, createSlice, createAsyncThunk, nanoid } from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import merge from 'lodash/merge'
import mergeWith from 'lodash/mergeWith'
import cloneDeep from 'lodash/cloneDeep'
import { RootState } from '.'
import { DeepPartial, MaybeApiError } from '@cfs-fixings/shared'
import mergeCopyArrays from '@/helpers/mergeCopyArrays'
import { validateDesign } from './design-validation'
import { PropertyTabs, setCurrentPropertiesTab } from './selected-properties-tab'

export type ActiveDesignSlice = {
  design?: DesignDetailResource
  selectedComponentId?: string
  saving: boolean
  replacing: boolean
  savedAt?: string
  errors: {
    [key: string]: string
  }[]
}

const initialState: ActiveDesignSlice = {
  design: undefined,
  selectedComponentId: undefined,
  saving: false,
  replacing: false,
  savedAt: undefined,
  errors: [],
}

export const createComponentAction = createAsyncThunk<void, { details: Omit<PostComponentsApiArg['body'], 'design_id'> }, { state: RootState }>(
  'activeDesign/createComponent',
  async ({ details }, thunk) => {
    const design = thunk.getState().activeDesign.design

    if (!design) {
      return thunk.abort()
    }

    const id = nanoid()

    thunk.dispatch(activeDesignSlice.actions.addComponent({
      id,
      ...details as typeof activeDesignSlice.actions.addComponent.arguments[0],
    }))

    thunk.dispatch(activeDesignSlice.actions.setSelectedComponent(id))

    const result = await thunk.dispatch(serverApi.endpoints.postComponents.initiate({
      body: {
        design_id: design!.id,
        ...details,
      },
    }))

    if ('error' in result) {
      return thunk.abort()
    }

    thunk.dispatch(activeDesignSlice.actions.updateComponent({
      id,
      data: result.data.data,
    }))

    thunk.dispatch(activeDesignSlice.actions.setSelectedComponent(result.data.data.id))

    thunk.dispatch(validateDesign({ design: design.id }))
  },
)

export const updateComponentAction = createAsyncThunk<PatchComponentsByComponentApiResponse | MaybeApiError, { componentId: string, details: PatchComponentsByComponentApiArg['body'] }, { state: RootState }>(
  'activeDesign/updateComponent',
  async ({ componentId, details }, thunk) => {
    const design = thunk.getState().activeDesign.design

    if (!design) {
      return thunk.abort()
    }

    const currentComponent = design.components?.find(c => c.id === componentId)
    if (!currentComponent) {
      return thunk.abort()
    }

    if (JSON.stringify(currentComponent) === JSON.stringify(mergeWith(cloneDeep(currentComponent), details, mergeCopyArrays))) {
      return thunk.abort()
    }

    const componentPayload = details as typeof activeDesignSlice.actions.updateComponent.arguments[1]
    const componentPayloadKey = Object.keys(componentPayload)[0] as keyof Pick<ComponentResource, 'concrete_slab' | 'cast_in_anchor_channel' | 't_bolt'>

    const componentRollbackObject = {
      [componentPayloadKey]: currentComponent[componentPayloadKey],
    }

    thunk.dispatch(activeDesignSlice.actions.updateComponent({
      id: componentId,
      data: componentPayload,
    }))

    /**
     * For T-Bolt updates within a group, iterates over each component and dispatches updateComponentAction,
     * ensuring immediate Redux store updates for the entire group - unless the position is being updated
     */
    if (currentComponent.group_id && !details.position) {
      const groupComponents = design.components?.filter(c => c.group_id === currentComponent.group_id && c.id !== componentId)
      if (groupComponents.length) {
        groupComponents.forEach((component) => {
          thunk.dispatch(activeDesignSlice.actions.updateComponent({
            id: component.id,
            data: componentPayload,
          }))
        })
      }
    }

    const result = await thunk.dispatch(serverApi.endpoints.patchComponentsByComponent.initiate({
      component: componentId,
      body: componentPayload,
    }))

    if ('error' in result) {
      thunk.dispatch(activeDesignSlice.actions.updateComponent({
        id: componentId,
        data: componentRollbackObject,
      }))

      return result.error as MaybeApiError
    }

    if (details.cast_in_anchor_channel?.length || details.cast_in_anchor_channel?.custom_length) {
      const newChannelLength = details.cast_in_anchor_channel.length ?? details.cast_in_anchor_channel.custom_length

      const affectedTBolts = design.components?.filter(c => c.parent_id === componentId && c.position?.x && c.position?.x > newChannelLength)

      affectedTBolts?.forEach(tBolt => {
        thunk.dispatch(activeDesignSlice.actions.removeComponent(tBolt.id))
      })
    }

    thunk.dispatch(validateDesign({ design: design.id }))

    return result.data as PatchComponentsByComponentApiResponse
  },
)

export const replaceCastInChannelAction = createAsyncThunk<void, { componentId: string, body: PatchComponentsByComponentReplaceCastInChannelApiArg['body'] }, { state: RootState }>(
  'activeDesign/replaceCastInChannel',
  async ({ componentId, body }, thunk) => {
    const design = thunk.getState().activeDesign.design

    if (!design) {
      return thunk.abort()
    }

    const result = await thunk.dispatch(serverApi.endpoints.patchComponentsByComponentReplaceCastInChannel.initiate({
      component: componentId,
      body,
    }))

    if ('error' in result) {
      return thunk.abort()
    }

    thunk.dispatch(activeDesignSlice.actions.setActiveDesign(
      result.data.data,
    ))

    thunk.dispatch(validateDesign({ design: design.id }))
  },
)

export const replaceTBoltAction = createAsyncThunk<void, { componentId: string, body: PatchComponentsByComponentReplaceTBoltApiArg['body'] }, { state: RootState }>(
  'activeDesign/replaceTBolt',
  async ({ componentId, body }, thunk) => {
    const design = thunk.getState().activeDesign.design

    if (!design) {
      return thunk.abort()
    }

    const result = await thunk.dispatch(serverApi.endpoints.patchComponentsByComponentReplaceTBolt.initiate({
      component: componentId,
      body,
    }))

    if ('error' in result) {
      return thunk.abort()
    }

    thunk.dispatch(activeDesignSlice.actions.setActiveDesign(
      result.data.data,
    ))

    thunk.dispatch(validateDesign({ design: design.id }))
  },
)

export const deleteComponentAction = createAsyncThunk<void, string, { state: RootState }>(
  'activeDesign/deleteComponent',
  async (componentId, thunk) => {
    const design = thunk.getState().activeDesign.design

    if (!design) {
      return thunk.abort()
    }

    const component = design.components?.find(c => c.id === componentId)
    if (!component) {
      return thunk.abort()
    }

    const children = design.components?.filter(c => c.parent_id === componentId)
    children?.forEach(child => {
      thunk.dispatch(activeDesignSlice.actions.removeComponent(child.id))
    })

    const nextComponent = design.components?.find(c => c.parent_id === component.parent_id && c.id !== componentId)

    thunk.dispatch(activeDesignSlice.actions.deselectComponent())

    thunk.dispatch(activeDesignSlice.actions.removeComponent(componentId))

    if (nextComponent) {
      thunk.dispatch(activeDesignSlice.actions.setSelectedComponent(nextComponent.id))
      thunk.dispatch(setCurrentPropertiesTab(PropertyTabs.Selected))
    } else {
      thunk.dispatch(setCurrentPropertiesTab(PropertyTabs.Results))
    }

    await thunk.dispatch(serverApi.endpoints.deleteComponentsByComponent.initiate({
      component: componentId,
      body: {},
    }))

    thunk.dispatch(validateDesign({ design: design.id }))
  },
)

export const activeDesignSlice = createSlice({
  name: 'activeDesign',
  initialState,
  reducers: {
    setActiveDesign: (state, action: PayloadAction<DesignDetailResource>) => {
      state.design = action.payload
    },
    setSelectedComponent: (state, action: PayloadAction<string>) => {
      state.selectedComponentId = action.payload
    },
    deselectComponent: (state) => {
      state.selectedComponentId = undefined
    },
    addComponent: (state, action: PayloadAction<PostComponentsApiResponse['data']>) => {
      if (!state.design) {
        return
      }

      state.design.components?.push(action.payload)
    },
    updateComponent: (state, action: PayloadAction<{id: string, data: DeepPartial<ComponentResource>}>) => {
      const componentIndex = state.design?.components?.findIndex(c => c.id === action.payload.id)
      state.design!.components![componentIndex!] = merge(state.design!.components![componentIndex!], action.payload.data)
    },
    replaceAnchorSpacings: (state, action: PayloadAction<number[]>) => {
      const castInAnchorChannel = state.design?.components.find((component) => {
        return Object.prototype.hasOwnProperty.call(component, 'cast_in_anchor_channel')
      })

      if (!castInAnchorChannel) {
        return
      }

      castInAnchorChannel.cast_in_anchor_channel!.anchor_spacings = action.payload

      state.design = {
        ...state.design!,
        components: state.design!.components?.map((component) => component.id === castInAnchorChannel.id ? castInAnchorChannel : component),
      }
    },
    removeComponent: (state, action: PayloadAction<string>) => {
      if (!state.design) {
        return
      }

      state.design.components = state.design.components?.filter(c => c.id !== action.payload)
    },
    setErrors: (state, action: PayloadAction<ActiveDesignSlice['errors']>) => {
      state.errors = action.payload
    },
    mergeErrors: (state, action: PayloadAction<ActiveDesignSlice['errors']>) => {
      state.errors = merge(state.errors, action.payload)
    },
    removeError: (state, action: PayloadAction<string>) => {
      const errorIndex = Object.entries(state.errors).findIndex(([key]) => key === action.payload)
      delete state.errors[errorIndex]
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateComponentAction.pending, (state) => {
        state.saving = true
      })
      .addCase(updateComponentAction.fulfilled, (state) => {
        state.savedAt = dayjs().toISOString()
        state.saving = false
      })

    builder
      .addCase(createComponentAction.pending, (state) => {
        state.saving = true
      })
      .addCase(createComponentAction.fulfilled, (state) => {
        state.savedAt = dayjs().toISOString()
        state.saving = false
      })

    builder
      .addCase(replaceCastInChannelAction.pending, (state) => {
        state.saving = true
        state.replacing = true
      })
      .addCase(replaceCastInChannelAction.fulfilled, (state) => {
        state.savedAt = dayjs().toISOString()
        state.saving = false
        state.replacing = false
      })

    builder
      .addCase(replaceTBoltAction.pending, (state) => {
        state.saving = true
        state.replacing = true
      })
      .addCase(replaceTBoltAction.fulfilled, (state) => {
        state.savedAt = dayjs().toISOString()
        state.saving = false
        state.replacing = false
      })
  },
})

export const { setActiveDesign, setSelectedComponent, deselectComponent, setErrors, mergeErrors, removeError, replaceAnchorSpacings, addComponent } = activeDesignSlice.actions

export default activeDesignSlice.reducer
